import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Sparkles, AlertCircle, Mail, MessageSquare, Phone } from "lucide-react";
import { cn } from "@/lib/utils";

interface Sequence {
  id: string;
  name: string;
  channels: string[];
  status: string;
}

interface OutreachSetupStepProps {
  sequences: Sequence[] | undefined;
  selectedSequenceId: string | null;
  onSelectSequence: (sequenceId: string) => void;
  channels: { email: boolean; sms: boolean; whatsapp: boolean };
  onToggleChannel: (channel: 'email' | 'sms' | 'whatsapp') => void;
  selectedLeadCount: number;
  error?: string;
}

export function OutreachSetupStep({
  sequences,
  selectedSequenceId,
  onSelectSequence,
  channels,
  onToggleChannel,
  selectedLeadCount,
  error
}: OutreachSetupStepProps) {
  const channelConfig = [
    { id: 'email' as const, label: 'Email', icon: Mail, color: 'bg-blue-500/20 text-blue-500' },
    { id: 'sms' as const, label: 'SMS', icon: MessageSquare, color: 'bg-green-500/20 text-green-500' },
    { id: 'whatsapp' as const, label: 'WhatsApp', icon: Phone, color: 'bg-emerald-500/20 text-emerald-500' },
  ];

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground">
        Configure AI-powered outreach for {selectedLeadCount} selected leads. Choose channels and sequence.
      </p>
      
      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      
      <div className="space-y-3">
        <Label>Select Channels</Label>
        {channelConfig.map(channel => {
          const ChannelIcon = channel.icon;
          return (
            <Card key={channel.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn("w-10 h-10 rounded-full flex items-center justify-center", channel.color)}>
                    <ChannelIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-medium">{channel.label}</div>
                    <div className="text-sm text-muted-foreground">AI-personalized messaging</div>
                  </div>
                </div>
                <Switch 
                  checked={channels[channel.id]}
                  onCheckedChange={() => onToggleChannel(channel.id)}
                />
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      <div className="space-y-2">
        <Label>Select Sequence</Label>
        <Select value={selectedSequenceId || ''} onValueChange={onSelectSequence}>
          <SelectTrigger>
            <SelectValue placeholder="Choose an outreach sequence" />
          </SelectTrigger>
          <SelectContent>
            {sequences?.filter(s => s.status === 'active').map(seq => (
              <SelectItem key={seq.id} value={seq.id}>
                {seq.name}
              </SelectItem>
            ))}
            {(!sequences || sequences.filter(s => s.status === 'active').length === 0) && (
              <SelectItem value="new" disabled>No active sequences</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>
      
      {(channels.email || channels.sms || channels.whatsapp) && selectedSequenceId && (
        <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg animate-fade-in">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-sm">
            Ready to enroll {selectedLeadCount} leads in outreach sequence
          </span>
        </div>
      )}
    </div>
  );
}
